import { useState } from "react"

import type { KenjossetChrome, KenjossetNavActive } from "@/components/kenjosset/types"
import Cart from "@/components/kenjosset/Cart"

interface NavbarProps {
  chrome: KenjossetChrome
  active?: KenjossetNavActive
}

// Class strings are written out in full (never interpolated) so Tailwind's JIT
// scanner keeps them.
const DESKTOP_LINK = "btn-link"
const DESKTOP_LINK_ACTIVE = "btn-link !text-contrast after:!bg-contrast"
const MOBILE_LINK = "btn-mobile-link !text-3xl !font-extrabold"
const MOBILE_LINK_ACTIVE =
  "btn-mobile-link !text-3xl !font-extrabold !text-contrast after:!bg-contrast"

// Faithful React port of kenjosset/common/_navbar. The CSS-only mobile drawer
// (label_tag + check_box_tag + peer-checked) becomes React state. Nav links stay
// plain anchors: /store and the item pages are still rendered by Slim.
export default function Navbar({ chrome, active }: NavbarProps) {
  const { paths, images, cart } = chrome
  const [mobileOpen, setMobileOpen] = useState(false)

  const links: { label: string; href: string; key: KenjossetNavActive }[] = [
    { label: "PORTRAIT", href: paths.about, key: "about" },
    { label: "GALERIE", href: paths.portfolio, key: "portfolio" },
    { label: "BOUTIQUE", href: paths.store, key: "store" },
  ]

  return (
    <>
      <div className="absolute top-1 right-8 z-40">
        <Cart cart={cart} basketSrc={images.basket} />
      </div>

      {/* Desktop */}
      <div className="w-full hidden md:flex bg-light relative cursor-default justify-between pl-12 py-6 pr-20">
        <a href={paths.root} className="flex flex-col items-center">
          <img src={images.logo} alt="Ken Josset" className="h-20" />
        </a>
        <div className="flex flex-col gap-1">
          {links.map((link) => (
            <a
              key={link.key}
              href={link.href}
              className={active === link.key ? DESKTOP_LINK_ACTIVE : DESKTOP_LINK}
            >
              {link.label}
            </a>
          ))}
        </div>
      </div>

      {/* Mobile */}
      <div className="h-36 md:hidden">
        <a href={paths.root} className="flex flex-col items-start pt-6 px-6">
          <img src={images.logo} alt="Ken Josset" className="h-16" />
        </a>

        <button
          type="button"
          aria-label="Menu"
          className="absolute z-40 top-20 right-6 rounded-full"
          onClick={() => setMobileOpen(true)}
        >
          <div className="text-primary px-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-10 h-10"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
          </div>
        </button>

        {mobileOpen && (
          <div className="z-50 fixed top-0 left-0 right-0 bottom-0 bg-light">
            <button
              type="button"
              aria-label="Fermer"
              className="absolute top-6 right-6"
              onClick={() => setMobileOpen(false)}
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full p-3 text-primary">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-12 h-12"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </button>
            <div className="flex flex-col justify-around items-start py-12 px-12 space-y-3">
              {links.map((link) => (
                <a
                  key={link.key}
                  href={link.href}
                  className={active === link.key ? MOBILE_LINK_ACTIVE : MOBILE_LINK}
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
